<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { ref } from 'vue'
import { cn } from '@/lib/utils'
import CardHeader from '@/components/ui/card/CardHeader.vue'
import Button from '@/components/ui/button/Button.vue'
import { Euro, GripVertical, Percent, Settings as SettingsIcon } from 'lucide-vue-next'
import { Dialog, DialogActionsBar } from '@progress/kendo-vue-dialogs'
import type { RangeColor } from '@/lib/types'
import RangeSettings from '@/components/settings/RangeSettings.vue'
import BaseRepository from '@/repository/BaseRepository'
import { usePercentageMode } from '@/lib/usePercentageMode'
import CardContent from './CardContent.vue'
import html2canvas from 'html2canvas'

interface Props {
  class?: HTMLAttributes['class']
  draggable?: boolean
  title?: string
  settings?: Record<string, RangeColor[]> | null
  settingsUrl?: string
  percentageMode?: boolean
  percentIcon?: boolean
  downloadChart?: {
    selector: string
    filename: string
  }
  downloadExcel?: () => void
}

const props = withDefaults(defineProps<Props>(), {
  draggable: true,
  percentageMode: false,
  percentIcon: false,
})

const dialogVisible = ref<boolean>(false)
const downloadProcessing = ref<boolean>(false)
const { isPercentageMode, togglePercentageMode } = usePercentageMode(false)
const emit = defineEmits(['drag-start', 'drag-end', 'update:percentageMode'])

const toggleDialog = () => {
  dialogVisible.value = !dialogVisible.value
}

const handleDragStart = () => {
  emit('drag-start')
}

const handleDragEnd = () => {
  emit('drag-end')
}

const changeMode = () => {
  togglePercentageMode()
  emit('update:percentageMode', isPercentageMode.value)
}

const saveSettings = async () => {
  if (!props.settings || !props.settingsUrl) return
  await BaseRepository.saveSettings(props.settingsUrl, props.settings)
  toggleDialog()
}

const handleChartDownload = async () => {
  if (!props.downloadChart) return

  const chartElement = document.querySelector(props.downloadChart.selector) as HTMLElement
  downloadProcessing.value = true
  if (chartElement) {
    const canvas = await html2canvas(chartElement)
    const dataUrl = canvas.toDataURL('image/png')
    const link = document.createElement('a')
    link.href = dataUrl
    link.download = props.downloadChart.filename
    link.click()
  }
  downloadProcessing.value = false
}
</script>

<template>
  <div :class="cn('rounded-xl border bg-card text-card-foreground shadow h-full', props.class)" @dragover.prevent @drop.prevent>
    <CardHeader v-if="props.title" class="flex justify-between items-center">
      <div v-if="props.title" class="font-bold">
        <h3>{{ props.title }}</h3>
      </div>
      <div class="flex items-center gap-2">
        <Button v-if="props.downloadExcel" variant="outline" @click="props.downloadExcel()">
          Télécharger excel
        </Button>

        <Button
          v-if="downloadChart"
          variant="outline"
          :loading="downloadProcessing"
          @click="handleChartDownload"
        >
          Télécharger
        </Button>

        <slot name="actions"></slot>
        <Button v-if="percentIcon" variant="outline" @click="changeMode">
          <Percent v-if="!isPercentageMode" />
          <Euro v-else />
        </Button>
        <Button v-if="settings && settingsUrl" @click="toggleDialog" variant="outline">
          <SettingsIcon />
        </Button>
        <Button
          v-if="draggable"
          variant="outline"
          class="cursor-move drag-handle p-2"
          :draggable="true"
          @dragstart="handleDragStart"
          @dragend="handleDragEnd"
        >
          <GripVertical class="h-4 w-4" />
        </Button>
      </div>
    </CardHeader>

    <CardContent class="mt-3">
      <slot></slot>
    </CardContent>

    <Dialog
      width="750px"
      v-if="dialogVisible"
      title="Paramètres"
      @close="toggleDialog"
      :initial-height="350"
    >
      <div class="flex gap-5 p-5" v-if="settings">
        <RangeSettings :settings="settings as Record<string, RangeColor[]>" />
      </div>
      <DialogActionsBar>
        <div class="text-right px-5 py-3">
          <Button @click="toggleDialog" variant="link"> Annuler</Button>
          <Button @click="saveSettings">Sauvegarder</Button>
        </div>
      </DialogActionsBar>
    </Dialog>
  </div>
</template>

<style scoped>
.drag-handle {
  opacity: 0.5;
  transition: opacity 0.2s;
  user-select: none;
  -webkit-user-drag: none;
  pointer-events: auto;
}

.drag-handle:hover {
  opacity: 1;
}

table tr td:first-child {
  padding: 0 25px 0 0;
}
</style>
